import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import api from './axiosConfig';
import { 
  Box, 
  Paper, 
  Typography, 
  CircularProgress, 
  Alert, 
  Grid, 
  Button, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Chip,
  Tooltip,
  IconButton,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  Edit, 
  TrendingUp, 
  TrendingDown, 
  People, 
  AttachMoney, 
  Inventory, 
  Assessment,
  HelpOutline,
  Visibility,
  VisibilityOff,
  Info,
  Warning,
  CheckCircle,
  ArrowBack
} from '@mui/icons-material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Divider, FormControl, InputLabel, Select, MenuItem, FormControlLabel as MuiFormControlLabel, Checkbox } from '@mui/material';
import DatePickerComponent from './DatePickerComponent';
import { Bar, Line } from 'react-chartjs-2';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import GaugeChart from 'react-gauge-chart';
import KPIBox from './KPIBox';
import { Chart, LinearScale, CategoryScale, BarController, BarElement, LineController, LineElement, PointElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { 
  PoundSterling,     // £ symbol
  Wallet,            // Wallet icon
  CreditCard,        // Credit card
  Banknote,          // Money/banknote
  Calculator,        // Calculator
  PiggyBank          // Savings/piggy bank
} from 'lucide-react';

// Register all the necessary chart components
Chart.register(
  LinearScale, 
  CategoryScale, 
  BarController, 
  BarElement, 
  LineController, 
  LineElement, 
  PointElement, 
  Title, 
  ChartTooltip, 
  Legend
);

// Custom UK Pound Icon Component
const PoundIcon = ({ sx, ...props }) => (
  <Box sx={{ 
    fontWeight: 'bold', 
    fontSize: '1.5rem', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    width: '1em',
    height: '1em',
    color: 'inherit',
    ...sx 
  }} {...props}>
    £
  </Box>
);

// Simplified Metric Card Component
const MetricCard = ({ title, value, growth, prefix = '', suffix = '', helpText, icon: Icon, color = 'primary', isGood = null }) => {
  const [showHelp, setShowHelp] = useState(false);
  
  const formatValue = (val) => {
    if (typeof val !== 'number') return 'N/A';
    if (prefix === '£') {
      return `£${val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    return val.toLocaleString();
  };

  const getGrowthColor = () => {
    if (!growth || growth === 0) return 'text.secondary';
    if (isGood === null) return growth > 0 ? 'success.main' : 'error.main';
    return (growth > 0) === isGood ? 'success.main' : 'error.main';
  };

  const getGrowthIcon = () => {
    if (!growth || growth === 0) return null;
    return growth > 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />;
  };

  return (
    <Card sx={{ height: '100%', position: 'relative' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {Icon && <Icon color={color} />}
            <Typography variant="subtitle2" color="text.secondary">
              {title}
            </Typography>
          </Box>
          {helpText && (
            <Tooltip title={helpText} arrow>
              <IconButton size="small" sx={{ ml: 'auto' }}>
                <HelpOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        <Typography variant="h5" component="div" fontWeight="bold">
          {formatValue(value)}{suffix}
        </Typography>
        
        {growth && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <Box sx={{ color: getGrowthColor(), display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {getGrowthIcon()}
              <Typography variant="caption" fontWeight="medium">
                {Math.abs(growth).toFixed(1)}%
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              vs last period
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Quick Action Card Component
const QuickActionCard = ({ title, description, icon: Icon, color, onClick, disabled = false, badge = null }) => (
  <Card 
    sx={{ 
      height: '100%', 
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      transition: 'all 0.2s',
      '&:hover': disabled ? {} : {
        transform: 'translateY(-2px)',
        boxShadow: 3
      }
    }}
    onClick={disabled ? undefined : onClick}
  >
    <CardContent sx={{ textAlign: 'center', py: 3 }}>
      <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: `${color}.light`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            mx: 'auto'
          }}
        >
          <Icon sx={{ fontSize: 30, color: `${color}.main` }} />
        </Box>
        {badge && (
          <Chip 
            label={badge} 
            size="small" 
            color="error" 
            sx={{ position: 'absolute', top: -5, right: -10 }}
          />
        )}
      </Box>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </CardContent>
  </Card>
);

// Business Health Indicator Component
const BusinessHealthIndicator = ({ metrics }) => {
  const getHealthScore = () => {
    let score = 0;
    let factors = 0;
    
    if (metrics.wagesToTurnover) {
      factors++;
      score += metrics.wagesToTurnover.value <= metrics.wagesToTurnover.target ? 1 : 0.5;
    }
    
    if (metrics.retailToService) {
      factors++;
      score += metrics.retailToService.value >= metrics.retailToService.target ? 1 : 0.5;
    }
    
    if (metrics.operatingProfit) {
      factors++;
      score += metrics.operatingProfit.value >= metrics.operatingProfit.target ? 1 : 0.5;
    }
    
    return factors > 0 ? (score / factors) * 100 : 0;
  };

  const healthScore = getHealthScore();
  const getHealthColor = () => {
    if (healthScore >= 80) return 'success';
    if (healthScore >= 60) return 'warning';
    return 'error';
  };

  const getHealthMessage = () => {
    if (healthScore >= 80) return 'Your salon is performing excellently!';
    if (healthScore >= 60) return 'Good performance with room for improvement';
    return 'Some areas need attention';
  };

  return (
    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: `${getHealthColor()}.light` }}>
      <Typography variant="h6" gutterBottom>
        Business Health Score
      </Typography>
      <Typography variant="h3" fontWeight="bold" color={`${getHealthColor()}.main`}>
        {healthScore.toFixed(0)}%
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {getHealthMessage()}
      </Typography>
      <Button 
        size="small" 
        sx={{ mt: 2 }}
        endIcon={<HelpOutline />}
      >
        What does this mean?
      </Button>
    </Paper>
  );
};

// Company Profile Section Component
const CompanyProfileSection = ({ vatSummary }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    business_name: '',
    business_address_line1: '',
    business_address_line2: '',
    business_address_line3: '',
    postcode: '',
    phone_number: '',
    is_vat_registered: false,
    vat_number: '',
    vat_scheme: '',
    holiday_year_start_month: 1,
    holiday_year_start_day: 1,
    total_holiday_days: 28
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const vatSchemes = [
    { value: 'STANDARD_RATE', label: 'Standard Rate (20%)' },
    { value: 'FLAT_RATE', label: 'Flat Rate Scheme' },
    { value: 'FIRST_YEAR_FLAT_RATE', label: 'First Year Flat Rate' }
  ];

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/company/profile', {
          params: { v: new Date().getTime() },
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        setProfileData(response.data);
      } catch (error) {
        console.error('Error fetching company profile:', error);
        setError('Failed to load company profile');
      }
    };
    
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'holiday_year_start_month' || 
        name === 'holiday_year_start_day' || 
        name === 'total_holiday_days') {
      const numValue = parseInt(value);
      if (name === 'holiday_year_start_day' && (numValue < 1 || numValue > 31)) {
        return;
      }
      setProfileData(prev => ({
        ...prev,
        [name]: numValue
      }));
      return;
    }

    setProfileData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'is_vat_registered' && !checked ? {
        vat_scheme: '',
        vat_number: ''
      } : {})
    }));
  };

  const handleSubmit = async () => {
    try {
      await api.put('/company/profile', {
        ...profileData,
        password: confirmPassword
      });
      setIsEditing(false);
      setConfirmPassword('');
      setError('');
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    }
  };

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {profileData.business_name || 'Your Salon'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Business Information & Settings
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => setIsEditing(true)}
            startIcon={<Edit/>}
          >
            Edit
          </Button>
        </Box>

        {!isEditing && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Contact
                  </Typography>
                  <Typography>{profileData.phone_number}</Typography>
                  <Typography component="div" sx={{ mt: 1 }}>
                    {profileData.business_address_line1}<br />
                    {profileData.business_address_line2 && <>{profileData.business_address_line2}<br /></>}
                    {profileData.postcode}
                  </Typography>
                </Box>
                
                {profileData.is_vat_registered && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      VAT Details
                    </Typography>
                    <Typography>Number: {profileData.vat_number}</Typography>
                    <Typography>Scheme: {profileData.vat_scheme}</Typography>
                    <Button 
                      variant="text" 
                      size="small"
                      component={Link}
                      to="/vat_dashboard"
                      sx={{ mt: 1, p: 0 }}
                    >
                      View VAT Dashboard →
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        )}

        <Dialog 
          open={isEditing} 
          onClose={() => setIsEditing(false)} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            Edit Business Details
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Business Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Business Name"
                      name="business_name"
                      value={profileData.business_name}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 1"
                      name="business_address_line1"
                      value={profileData.business_address_line1}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Address Line 2"
                      name="business_address_line2"
                      value={profileData.business_address_line2}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Postcode"
                      name="postcode"
                      value={profileData.postcode}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone_number"
                      value={profileData.phone_number}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  VAT Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <MuiFormControlLabel
                      control={
                        <Checkbox
                          name="is_vat_registered"
                          checked={profileData.is_vat_registered}
                          onChange={handleChange}
                        />
                      }
                      label="Business is VAT Registered"
                    />
                  </Grid>
                  {profileData.is_vat_registered && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="VAT Number"
                          name="vat_number"
                          value={profileData.vat_number}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>VAT Scheme</InputLabel>
                          <Select
                            name="vat_scheme"
                            value={profileData.vat_scheme}
                            onChange={handleChange}
                            label="VAT Scheme"
                          >
                            {vatSchemes.map(scheme => (
                              <MenuItem key={scheme.value} value={scheme.value}>
                                {scheme.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  helperText="Please enter your password to save changes"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsEditing(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={!confirmPassword}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// Main MoneyDashboard Component
const MoneyDashboard = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'year').startOf('day'));
  const [endDate, setEndDate] = useState(dayjs().endOf('day'));
  const [timeFrame, setTimeFrame] = useState('custom');
  const [showAdvancedView, setShowAdvancedView] = useState(false);
  const [kpiTrendData, setKpiTrendData] = useState([]);

  // Calculate metrics for the gauge charts
  const calculateMetrics = (kpiData, revenueData) => {
    if (!kpiData || !revenueData) return {};
    
    const metrics = {};
    
    // Wages to Turnover calculation
    if (kpiData.wages && kpiData.employerNI && kpiData.pension && revenueData.annualTotal) {
      const wages = typeof kpiData.wages.value === 'number' ? kpiData.wages.value : 0;
      const employerNI = typeof kpiData.employerNI.value === 'number' ? kpiData.employerNI.value : 0;
      const employerPension = typeof kpiData.pension.value === 'number' ? kpiData.pension.value : 0;
      const totalWageCost = wages + employerNI + employerPension;
      const annualRevenue = revenueData.annualTotal || 0;
      
      if (annualRevenue > 0) {
        metrics.wagesToTurnover = {
          value: (totalWageCost / annualRevenue) * 100,
          target: 35,
          warning: 50
        };
      }
    }
    
    // Retail to Service calculation
    if (kpiData.retailSales && kpiData.serviceSales) {
      const retailSales = typeof kpiData.retailSales.value === 'number' ? kpiData.retailSales.value : 0;
      const serviceSales = typeof kpiData.serviceSales.value === 'number' ? kpiData.serviceSales.value : 0;
      
      if (serviceSales > 0) {
        metrics.retailToService = {
          value: (retailSales / serviceSales) * 100,
          target: 20,
          warning: 10
        };
      }
    }
    
    // Operating Profit calculation
    if (kpiData.totalCosts && revenueData.annualTotal) {
      const totalCosts = typeof kpiData.totalCosts.value === 'number' ? kpiData.totalCosts.value : 0;
      const annualRevenue = revenueData.annualTotal || 0;
      
      if (annualRevenue > 0) {
        metrics.operatingProfit = {
          value: ((annualRevenue - totalCosts) / annualRevenue) * 100,
          target: 15,
          warning: 8
        };
      }
    }
    
    return metrics;
  };

  // Time frame handling
  const handleTimeFrameChange = (newTimeFrame) => {
    setTimeFrame(newTimeFrame);
    
    const now = dayjs();
    let newStartDate, newEndDate;
    
    switch(newTimeFrame) {
      case 'ytd':
        newStartDate = dayjs().startOf('year');
        newEndDate = now;
        break;
      case 'last-quarter':
        const currentQuarter = Math.floor((now.month()) / 3);
        const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
        const year = currentQuarter === 0 ? now.year() - 1 : now.year();
        newStartDate = dayjs().year(year).month(lastQuarter * 3).startOf('month');
        newEndDate = newStartDate.add(3, 'month').subtract(1, 'day');
        break;
      case 'last-month':
        newStartDate = dayjs().subtract(1, 'month').startOf('month');
        newEndDate = dayjs().subtract(1, 'month').endOf('month');
        break;
      default:
        return;
    }
    
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    fetchDashboardData(newStartDate, newEndDate);
  };

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async (
    startDateParam = startDate, 
    endDateParam = endDate
  ) => {
    if (!isAuthenticated || user?.role !== 'ACCOUNT_OWNER') {
      console.warn('User not authenticated or not an account owner');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    const timestamp = new Date().getTime();
    
    try {
      const [kpiResponse, revenueResponse, requestsResponse, vatResponse, trendResponse] = 
        await Promise.all([
          api.get('/dashboard/kpi_data', { 
            params: {
              start_date: startDateParam.format('YYYY-MM-DD'), 
              end_date: endDateParam.format('YYYY-MM-DD'),
              v: timestamp
            }
          }),
          api.get('/dashboard/revenue_data', { 
            params: {
              start_date: startDateParam.format('YYYY-MM-DD'), 
              end_date: endDateParam.format('YYYY-MM-DD'),
              v: timestamp
            }
          }),
          api.get('/dashboard/pending_requests', {
            params: { v: timestamp }
          }),
          api.get('/vat/status', {
            params: { v: timestamp }
          }),
          api.get('/dashboard/kpi_trends', { 
            params: {
              start_date: startDateParam.format('YYYY-MM-DD'),
              end_date: endDateParam.format('YYYY-MM-DD'),
              v: timestamp
            }
          }).catch(err => {
            console.warn('KPI trends endpoint not available:', err);
            return { data: [] };
          })
        ]);
      
      setKpiTrendData(trendResponse.data || []);
      
      setDashboardData({
        kpiData: kpiResponse.data,
        revenueData: revenueResponse.data,
        pendingRequests: requestsResponse.data.pending_requests || 0,
        vatData: vatResponse.data
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, user, isAuthenticated, timeFrame]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'ACCOUNT_OWNER') {
      fetchDashboardData();
    }
  }, [isAuthenticated, user, fetchDashboardData]);

  if (!isAuthenticated || user?.role !== 'ACCOUNT_OWNER') {
    return <Navigate to="/unauthorized" replace />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading your money dashboard...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => fetchDashboardData(startDate, endDate)} variant="contained" sx={{ mt: 2 }}>Retry</Button>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No dashboard data available.</Alert>
        <Button onClick={() => fetchDashboardData(startDate, endDate)} variant="contained" sx={{ mt: 2 }}>Refresh Data</Button>
      </Box>
    );
  }

  const { 
    kpiData = {}, 
    revenueData = { monthlyData: [], annualTotal: 0 }, 
    pendingRequests = 0, 
    vatData 
  } = dashboardData;
  
  const kpiMetrics = calculateMetrics(kpiData, revenueData);

  // Create simplified metrics for display
  const keyMetrics = [
    {
      title: 'Monthly Revenue',
      value: revenueData.annualTotal / 12,
      growth: kpiData.serviceSales?.growth,
      prefix: '£',
      helpText: 'Average monthly revenue for the selected period',
      icon: PoundIcon,
      color: 'primary'
    },
    {
      title: 'Customer Count',
      value: kpiData.clientCount?.value || kpiData.customerCount?.value,
      growth: kpiData.clientCount?.growth || kpiData.customerCount?.growth,
      helpText: 'Total number of unique customers served',
      icon: People,
      color: 'success'
    },
    {
      title: 'Average Spend',
      value: kpiData.averageTicket?.value,
      growth: kpiData.averageTicket?.growth,
      prefix: '£',
      helpText: 'Average amount each customer spends per visit',
      icon: PoundIcon,
      color: 'info'
    },
    {
      title: 'Staff Costs',
      value: kpiData.wages?.value,
      growth: kpiData.wages?.growth,
      prefix: '£',
      helpText: 'Total staff wages for the period',
      icon: People,
      color: 'warning',
      isGood: false
    }
  ];

  const quickActions = [
    {
      title: 'My Team',
      description: 'Manage staff, training, and reviews',
      icon: People,
      color: 'primary',
      onClick: () => navigate('/employees'),
      badge: pendingRequests > 0 ? pendingRequests : null
    },
    {
      title: 'Money Tracking',
      description: 'View sales, commissions, and reports',
      icon: PoundIcon,
      color: 'success',
      onClick: () => navigate('/income')
    },
    {
      title: 'Stock Control',
      description: 'Manage products and inventory',
      icon: Inventory,
      color: 'warning',
      onClick: () => navigate('/stock')
    },
    {
      title: 'Business Reports',
      description: 'Analytics and performance insights',
      icon: Assessment,
      color: 'info',
      onClick: () => navigate('/reports')
    }
  ];

  // Chart data
  const barChartData = {
    labels: revenueData.monthlyData?.map(item => item.month) || [],
    datasets: [{
      label: 'Monthly Revenue',
      data: revenueData.monthlyData?.map(item => item.total) || [],
      backgroundColor: 'rgba(75,192,192,0.6)',
      borderColor: 'rgba(75,192,192,1)',
      borderWidth: 1
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `£${context.parsed.y.toLocaleString()}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `£${value.toLocaleString()}`
        }
      }
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header with Back Button */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              startIcon={<ArrowBack />}
              sx={{ mb: 2 }}
            >
              Back to Dashboard
            </Button>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PoundSterling size={32} /> Money Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Sales, commissions, and financial reports for your salon
              </Typography>
            </Box>
          </Box>
          
          <FormControlLabel
            control={
              <Switch
                checked={showAdvancedView}
                onChange={(e) => setShowAdvancedView(e.target.checked)}
                color="primary"
              />
            }
            label="Advanced View"
          />
        </Box>
        
        {/* Business Health Indicator */}
        {Object.keys(kpiMetrics).length > 0 && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <BusinessHealthIndicator metrics={kpiMetrics} />
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Financial Health Tip 💡
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Keep staff costs below 35% of revenue and aim for 20% retail sales to maintain healthy profits.
                    Your current business health score reflects how well you're meeting these targets.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Company Profile */}
      <CompanyProfileSection vatSummary={vatData} />

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Quick Actions
        </Typography>
        <Grid container spacing={3}>
          {quickActions.map((action, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <QuickActionCard {...action} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Key Metrics */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Key Financial Numbers
        </Typography>
        <Grid container spacing={3}>
          {keyMetrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <MetricCard {...metric} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Date Controls - Simplified */}
      {showAdvancedView && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Advanced Controls
          </Typography>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <DatePickerComponent 
                startDate={startDate} 
                endDate={endDate} 
                setStartDate={setStartDate} 
                setEndDate={setEndDate} 
              />
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button 
                  variant={timeFrame === 'ytd' ? 'contained' : 'outlined'}
                  onClick={() => handleTimeFrameChange('ytd')}
                  size="small"
                >
                  Year to Date
                </Button>
                <Button 
                  variant={timeFrame === 'last-quarter' ? 'contained' : 'outlined'}
                  onClick={() => handleTimeFrameChange('last-quarter')}
                  size="small"
                >
                  Last Quarter
                </Button>
                <Button 
                  variant={timeFrame === 'last-month' ? 'contained' : 'outlined'}
                  onClick={() => handleTimeFrameChange('last-month')}
                  size="small"
                >
                  Last Month
                </Button>
              </Box>

              <Button 
                onClick={() => fetchDashboardData(startDate, endDate)} 
                variant="contained"
                sx={{ ml: 'auto' }}
              >
                Refresh Data
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Revenue Chart */}
      {revenueData.monthlyData && revenueData.monthlyData.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Monthly Revenue Trend
          </Typography>
          <Paper sx={{ p: 3, height: 400 }}>
            <Bar data={barChartData} options={chartOptions} />
          </Paper>
        </Box>
      )}

      {/* Advanced KPI Section - Only show in advanced view */}
      {showAdvancedView && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Detailed Performance Metrics
          </Typography>
          
          {/* Financial Metrics */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Financial Overview</Typography>
                <Grid container spacing={2}>
                  {['serviceSales', 'retailSales', 'totalExpenditure', 'totalCosts'].map(key => {
                    if (!kpiData[key] || typeof kpiData[key].value !== 'number') return null;
                    return (
                      <Grid item xs={12} sm={6} key={key}>
                        <MetricCard
                          title={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          value={kpiData[key].value}
                          growth={kpiData[key].growth}
                          prefix="£"
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Staff Overview</Typography>
                <Grid container spacing={2}>
                  {['wages', 'employerNI', 'pension'].map(key => {
                    if (!kpiData[key] || typeof kpiData[key].value !== 'number') return null;
                    return (
                      <Grid item xs={12} sm={6} key={key}>
                        <MetricCard
                          title={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          value={kpiData[key].value}
                          growth={kpiData[key].growth}
                          prefix="£"
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Help Section */}
      <Paper sx={{ p: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Info />
          <Box>
            <Typography variant="h6" gutterBottom>
              Need help understanding your numbers?
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Start by ensuring your sales data is up to date, then review your key metrics regularly to spot trends.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" color="inherit" size="small" onClick={() => navigate('/income')}>
                Import Sales Data
              </Button>
              <Button variant="outlined" color="inherit" size="small" onClick={() => navigate('/reports')}>
                View Detailed Reports
              </Button>
              <Button variant="outlined" color="inherit" size="small">
                Financial Help Guide
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* VAT Dashboard Link */}
      {vatData && vatData.data && vatData.data.is_registered && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button 
            variant="contained" 
            color="primary"
            component={Link}
            to="/vat_dashboard"
            startIcon={<ReceiptIcon />}
            size="large"
          >
            View VAT Dashboard
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default MoneyDashboard;